import type { ProblemaApi } from '@tallerya/i18n';
import { api } from '../api';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';
import { useTallerDeLaCache } from '../taller/ProveedorTaller';
import { RAIZ_PERMISOS, TALLER_SUSPENDIDO } from './suspension';

// Qué puede hacer quien está viendo la pantalla.
//
// ## Ocultar no es proteger
//
// Esto decide qué se **muestra**, no qué se puede hacer. La comprobación que vale
// corre en el servidor, en cada petición, en el `AuthorizationBehavior`. Si
// alguien manipulara esta respuesta en el navegador, lo único que lograría es ver
// botones que le van a responder 403.
//
// Vale la pena tenerlo presente al escribir una pantalla: esconder un botón es
// cortesía —no ofrecer algo que va a ser negado—, nunca una medida de seguridad.
//
// ## Por qué TanStack Query y no un contexto propio
//
// Es la sección 4.3 del estándar: los datos del servidor van por acá. Y encaja:
// los permisos se piden una vez, se comparten entre todas las pantallas, y
// cambian sin avisar cuando el dueño del taller toca un rol. Un contexto con
// `useEffect` sería reimplementar la caché y la invalidación a mano.

/// La clave de caché. **Lleva el taller adentro**, igual que la del servidor.
///
/// No es ceremonia: en una computadora compartida, salir y entrar con otra
/// persona de otro taller sin recargar la página dejaría los permisos del
/// anterior si la clave fuera fija. Es la versión en el navegador de la regla 5
/// de multitenancy.
const clave = (taller: string | undefined) => [RAIZ_PERMISOS, taller ?? 'sin-sesion'] as const;

/// Cada cuánto se vuelve a preguntar mientras el taller está suspendido (#244):
/// lo mismo que la API guarda el estado del taller (`TenantStatusMiddleware`).
const ESPERA_SUSPENDIDO = 30 * 1000;

/// Los permisos de quien está en sesión.
///
/// Devuelve también `cargando`, y **hay que usarlo**: mientras la consulta viaja,
/// `puede()` responde `false` para todo. Una pantalla que no distinga los dos
/// estados parpadea —muestra «no tienes permiso» y un instante después el
/// contenido—, que es peor que esperar.
export function usePermisos() {
  const auth = useAuth();
  const taller = useTallerDeLaCache();
  const cache = useQueryClient();

  const consulta = useQuery({
    queryKey: clave(taller),

    // Sin sesión no se pregunta: la respuesta sería un 401 garantizado.
    enabled: auth.isAuthenticated,

    queryFn: async ({ queryKey }) => {
      const { data, error } = await api.GET('/api/me/permissions');

      // El taller suspendido es una respuesta, no un fallo (#244): un error lo
      // borra la siguiente consulta o lo pisa un corte de red, y con él se irían
      // el aviso y la consulta cada 30 s. Una respuesta se queda hasta que llega otra.
      if (error && (error as ProblemaApi).code === TALLER_SUSPENDIDO) {
        return { codes: [], suspendido: true };
      }

      // `error` acá es la respuesta de la API, no un fallo de red. El 403 de
      // `auth.no_membership` —a la persona la sacaron del taller y su token
      // todavía no vence— llega por este camino.
      if (error || !data) throw new Error('No se pudieron leer los permisos');

      // Lo reactivaron: lo que se pidió durante la suspensión quedó con su 403
      // —la sucursal del encabezado, por ejemplo—, y se vuelve a pedir.
      if (cache.getQueryData<{ suspendido: boolean }>(queryKey)?.suspendido) {
        void cache.invalidateQueries({ predicate: (q) => q.queryKey[0] !== RAIZ_PERMISOS });
      }

      return { codes: data.codes, suspendido: false };
    },

    // Cinco minutos, igual que el token. Es el mismo razonamiento que la caché
    // del servidor: no es el mecanismo para aplicar un cambio —quien cambia un
    // permiso invalida—, es la red de seguridad para lo que cambió por fuera.
    staleTime: 5 * 60 * 1000,

    // Con el taller suspendido, el aviso se va solo al reactivarlo (#244): se
    // vuelve a preguntar cada 30 s y al regresar a la pestaña. `always` porque la
    // respuesta de suspendido está fresca y, si no, el foco no la volvería a pedir.
    refetchInterval: (q) => (q.state.data?.suspendido ? ESPERA_SUSPENDIDO : false),
    refetchOnWindowFocus: (q) => (q.state.data?.suspendido ? 'always' : false),
  });

  const suspendido = consulta.data?.suspendido ?? false;
  const codigos = consulta.data?.codes ?? [];

  return {
    cargando: consulta.isPending && auth.isAuthenticated,
    error: consulta.error,

    /// El taller está suspendido (#243, #244). Cambia la pantalla por el aviso y
    /// deja el menú vacío.
    suspendido,

    /// El código se pasa como cadena porque el catálogo vive en el backend
    /// (`PermissionCodes`). **Un código inventado devuelve `false` para siempre y
    /// nada lo delata**, así que cuando el cliente generado exponga la lista de
    /// códigos, esto debería tiparse contra ella.
    puede: (codigo: string) => codigos.includes(codigo),

    codigos,
  };
}
